import {
  Alert,
  Box,
  CardActionArea,
  CardContent,
  Checkbox,
  Chip,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import GlassCard from "Components/GlassCard/GlassCard";
import { ApiMetric } from "@incendium/api";
import {
  METRICS_PRIMARY_INFO,
  METRICS_PRIMARY_SECTION_SUBTITLE,
  METRICS_PRIMARY_SECTION_TITLE,
  METRICS_SUPPORTING_SECTION_SUBTITLE,
  METRICS_SUPPORTING_SECTION_TITLE,
  RECOMMENDED_CHIP_LABEL,
} from "../helpers/copy";
import { PRIMARY_METRICS, SUPPORTING_METRICS } from "../helpers/metricOptions";
import { useIncrementalitySetupContext } from "../providers/IncrementalitySetupProvider";
import produce from "immer";
import {
  MetricCard,
  MetricIconBubble,
  SectionSubTitle,
  SectionTitle,
} from "./styled";

function IncrementalityMetricsTab() {
  const { incrementalityTest, setIncrementalityTest } =
    useIncrementalitySetupContext();
  const primaryMetricId = incrementalityTest.primaryMetric;
  const supportingMetricIds = incrementalityTest.supportingMetrics || [];

  const toggleSupportingMetric = (metricId: ApiMetric) => {
    setIncrementalityTest((prev) => {
      const current = prev.supportingMetrics || [];
      const next = produce(current, (draft) => {
        const exists = draft.includes(metricId);
        if (exists) {
          return draft.filter((id) => id !== metricId);
        }
        if (draft.length >= 6) {
          return draft;
        }
        draft.push(metricId);
      });
      return {
        ...prev,
        supportingMetrics: next,
      };
    });
  };

  const setPrimaryMetric = (metricId: ApiMetric) => {
    setIncrementalityTest((prev) =>
      produce(prev, (draft) => {
        draft.primaryMetric = metricId;
      }),
    );
  };

  return (
    <Stack spacing={2.5}>
      <Alert severity="info">{METRICS_PRIMARY_INFO}</Alert>

      <GlassCard boxProps={{ p: 2 }} noShadow>
        <SectionTitle gutterBottom>
          {METRICS_PRIMARY_SECTION_TITLE}
        </SectionTitle>
        <SectionSubTitle mb={2}>
          {METRICS_PRIMARY_SECTION_SUBTITLE}
        </SectionSubTitle>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(0, 1fr))",
              md: "repeat(5, minmax(0, 1fr))",
            },
            gap: 1.5,
          }}
        >
          {PRIMARY_METRICS.map((metric) => {
            const selected = primaryMetricId === metric.id;
            return (
              <Box key={metric.id}>
                <MetricCard $selected={selected}>
                  <CardActionArea
                    onClick={() => setPrimaryMetric(metric.id)}
                    sx={{ height: "100%", width: "100%", display: "block" }}
                  >
                    <CardContent sx={{ height: "100%" }}>
                      <Stack spacing={1}>
                        <MetricIconBubble>{metric.icon}</MetricIconBubble>
                        <Typography fontWeight={600} variant="body1">
                          {metric.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {metric.description}
                        </Typography>
                        {metric.recommended && (
                          <Chip
                            size="small"
                            color="success"
                            label={RECOMMENDED_CHIP_LABEL}
                          />
                        )}
                      </Stack>
                    </CardContent>
                  </CardActionArea>
                </MetricCard>
              </Box>
            );
          })}
        </Box>
      </GlassCard>

      <GlassCard boxProps={{ p: 2 }} noShadow>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Box>
            <SectionTitle>{METRICS_SUPPORTING_SECTION_TITLE}</SectionTitle>
            <SectionSubTitle>
              {METRICS_SUPPORTING_SECTION_SUBTITLE}
            </SectionSubTitle>
          </Box>
          <Chip label={`${supportingMetricIds.length} selected`} size="small" />
        </Box>
        <Grid container spacing={1.5}>
          {SUPPORTING_METRICS.map((metric) => {
            const checked = supportingMetricIds.includes(metric.id);
            return (
              <Grid item xs={12} md={6} key={metric.id}>
                <MetricCard $selected={checked}>
                  <CardActionArea
                    onClick={() => toggleSupportingMetric(metric.id)}
                    sx={{ height: "100%", width: "100%", display: "block" }}
                  >
                    <CardContent sx={{ height: "100%" }}>
                      <Stack
                        direction="row"
                        spacing={2}
                        alignItems="flex-start"
                      >
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Checkbox
                            checked={checked}
                            size="small"
                            sx={{ mt: -0.75, ml: -1 }}
                          />

                          <MetricIconBubble>{metric.icon}</MetricIconBubble>
                        </Stack>
                        <Box>
                          <Typography fontWeight={600} variant="body1">
                            {metric.label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {metric.description}
                          </Typography>
                        </Box>
                      </Stack>
                      {metric.category && (
                        <Chip
                          size="small"
                          label={metric.category}
                          sx={{ mt: 1 }}
                        />
                      )}
                    </CardContent>
                  </CardActionArea>
                </MetricCard>
              </Grid>
            );
          })}
        </Grid>
      </GlassCard>
    </Stack>
  );
}

export default IncrementalityMetricsTab;
